/* Project SWIFT — logged-out "quick refill preview" animation.
 *
 * Self-contained, data-free loop driving the 3-step mock in
 * sections/swift-login-gate.liquid. It only toggles data-* attributes and a
 * couple of CSS custom properties; all visuals live in assets/swift.css.
 *
 * Steps:
 *   1. Your refills  -> cursor taps a product, its pack-size menu opens, taps
 *                       "12" (selection moves 6 -> 12), then the menu closes
 *   2. Add extras    -> cursor taps add, a free replacement pump drops in
 *   3. Checkout      -> cursor taps Checkout (pressed + click ping), then
 *                       "Ships within 1 business day" reveals
 *
 * Accessibility: the mock is role="img" with an aria-label summary, and every
 * moving part is aria-hidden in the markup. We do NOT animate for users who
 * prefer reduced motion — swift.css renders the full story statically instead.
 * Performance: a single rAF-free setTimeout chain; pauses when off-screen.
 */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function initPreview(root) {
    if (!root || root.dataset.swiftPreviewInit === '1') return;
    root.dataset.swiftPreviewInit = '1';

    // Reduced-motion users get the static composed view from CSS — leave as-is.
    if (reduceMotion) return;

    root.removeAttribute('data-static');

    var stage = root.querySelector('.swift-login-concept__stage');
    var cursor = root.querySelector('.swift-login-concept__cursor');

    // Targets the cursor "taps" on, per step.
    var productTarget = root.querySelector('.swift-login-concept__row--active');
    var packTarget = root.querySelector('.swift-login-concept__pack[data-pack="12"]');
    var addTarget = root.querySelector('.swift-login-concept__extra-row--add');
    var checkoutTarget = root.querySelector('.swift-login-concept__checkout-btn');

    var timers = [];
    var running = false;

    function clearTimers() {
      timers.forEach(function (t) { clearTimeout(t); });
      timers = [];
    }

    function later(fn, ms) {
      timers.push(setTimeout(fn, ms));
    }

    // Move the cursor over an element (coords relative to the stage).
    function moveCursorTo(el) {
      if (!cursor || !stage || !el) return;
      var s = stage.getBoundingClientRect();
      var r = el.getBoundingClientRect();
      var x = r.left - s.left + r.width / 2 - cursor.offsetWidth / 2;
      var y = r.top - s.top + r.height / 2 - cursor.offsetHeight / 2;
      cursor.style.setProperty('--cursor-x', x + 'px');
      cursor.style.setProperty('--cursor-y', y + 'px');
    }

    function press() {
      root.setAttribute('data-pressed', '');
      later(function () { root.removeAttribute('data-pressed'); }, 320);
    }

    // One full loop of the sequence.
    function runCycle() {
      if (!running) return;
      clearTimers();
      root.removeAttribute('data-added');
      root.removeAttribute('data-shipped');
      root.removeAttribute('data-menu-open');
      root.setAttribute('data-pack-choice', '6');

      // Cursor glides take ~1s (see .swift-login-concept__cursor transition), so
      // each "tap" is scheduled >1s after its moveCursorTo to land on arrival.

      // STEP 1 — refills shown; tap a product, swap its pack size 6 -> 12
      root.setAttribute('data-active-step', '1');
      later(function () { moveCursorTo(productTarget); }, 250);
      later(function () { press(); }, 1500);                              // tap the product
      later(function () { root.setAttribute('data-menu-open', ''); }, 1700); // pack menu opens
      later(function () { moveCursorTo(packTarget); }, 2000);
      later(function () { press(); }, 3200);                              // tap "12"
      later(function () { root.setAttribute('data-pack-choice', '12'); }, 3320);
      later(function () { root.removeAttribute('data-menu-open'); }, 3800); // menu closes

      // STEP 2 — add an extra (cursor taps one, it flips to "Added")
      later(function () {
        root.setAttribute('data-active-step', '2');
        moveCursorTo(addTarget);
      }, 4400);
      later(function () { press(); }, 5600);
      later(function () { root.setAttribute('data-added', ''); }, 5750);   // extra marked added

      // STEP 3 — checkout tapped, ships fast
      later(function () {
        root.setAttribute('data-active-step', '3');
        moveCursorTo(checkoutTarget);
      }, 6500);
      later(function () { press(); }, 7700);
      later(function () { root.setAttribute('data-shipped', ''); }, 8000);

      // loop
      later(runCycle, 10400);
    }

    function start() {
      if (running) return;
      running = true;
      runCycle();
    }

    function stop() {
      running = false;
      clearTimers();
    }

    // Only animate while visible; saves work when scrolled away / tab hidden.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !document.hidden) {
              start();
            } else {
              stop();
            }
          });
        },
        { threshold: 0.25 }
      );
      io.observe(root);
    } else {
      start();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else if (isInViewport(root)) {
        start();
      }
    });

    // Reposition the cursor on resize so taps stay aligned.
    window.addEventListener('resize', function () {
      var active = root.getAttribute('data-active-step');
      if (active === '1') moveCursorTo(root.hasAttribute('data-menu-open') ? packTarget : productTarget);
      else if (active === '2') moveCursorTo(addTarget);
      else if (active === '3') moveCursorTo(checkoutTarget);
    });
  }

  function isInViewport(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  ready(function () {
    var nodes = document.querySelectorAll('[data-swift-login-preview]');
    Array.prototype.forEach.call(nodes, initPreview);
  });
})();
